import React from 'react'
import { Card, CriticalRadio, RadioGroup, Select } from '../../atoms'
import { connect } from 'react-redux'
import { useEntities } from '../helpers/selectors'
import { FsCore } from 'types/fs-core'
import RichText from '../../components/rich-text-block'
import { useESData } from '../../redux/elasticsearch/elasticsearch-selectors'

interface Content {
  data: object
  content: Array<Content>
  nodeType: string
}
interface RadioChild {
  name: string
  title?: string
  content: Content
}
interface RadioSelectorProps {
  sys?: { id: string }
  name: string
  brand?: 'money' | 'uswitch' | 'confused'
  title: string
  children: RadioChild[]
  hasEligibleHits: boolean
  selectedRadio: string
}

const nothing = () => {}

const getRadioIndex = (selectedRadio: string | undefined, options: RadioChild[]): number => {
  const selectedIndex = options.findIndex((option) => option.name && option.name === selectedRadio)
  return selectedIndex !== -1 ? selectedIndex : 0
}

const RadioSelector = ({
  title,
  brand = 'uswitch',
  hasEligibleHits,
  selectedRadio,
  ...props
}: RadioSelectorProps) => {
  const children = props.children ? useEntities(props.children) : []
  const defaultChecked = getRadioIndex(selectedRadio, children)

  const eligibleProductTables = useEntities(
    children
      .filter(
        (child) =>
          child.sys?.contentTypeId === 'richTextBlock' &&
          child.contentTwo?.content?.[0]?.nodeType === 'embedded-entry-block',
      )
      .map((child) => child.contentTwo?.content?.[0]?.data?.target),
  ).filter((pt) => pt.variant === 'eligibility')

  const counts = {}
  eligibleProductTables.forEach(({ title, dataset }) => {
    if (hasEligibleHits) {
      const esData = useESData(dataset.sys.id)
      const total = esData?.hits?.total
      counts[title] = typeof total === 'number' ? total : total?.value || 0
    } else {
      counts[title] = 0
    }
  })

  const dataClickTracking = {
    component_category: 'component',
    component_id: props.sys?.id,
    component_name: 'radioSelector',
    contentful_name: props.name,
    contentful_variant: null,
  }

  return (
    <div data-radio-selector={title}>
      <Card className='RadioSelector'>
        <div className='ProductNavigation__inner-card'>
          <h2 className='RadioSelector__title'>{title}</h2>
          <Select
            name='selection'
            value={defaultChecked.toString()}
            options={
              children &&
              children.map(({ name }, index) => ({
                value: index,
                text: `${name} (${counts[name] || 0})`,
              }))
            }
            onChange={nothing}
            brand={brand}
            buttonActions={{
              'data-select-tracking': JSON.stringify({
                ...dataClickTracking,
                selectEventName: 'Selected',
                additionalProps: { title },
              }),
            }}
          />
          <div className='RadioSelector__wrapper'>
            <RadioGroup stack={true}>
              {children &&
                children.map(({ name }, index) => {
                  const id = name.replaceAll(/[^A-Za-z0-9_-]/g, '')
                  return (
                    <CriticalRadio
                      key={props.sys?.id}
                      onClick={nothing}
                      name={`radio-selector-${id}`}
                      centerLabel={true}
                      checked={index === defaultChecked}
                      buttonActions={{
                        /* eslint-disable camelcase */
                        'data-radio-selector-button': index,
                        'data-click-tracking': JSON.stringify({
                          ...dataClickTracking,
                          event_name: 'Selected',
                          additionalProps: {
                            title,
                            index,
                          },
                        }),
                      }}
                    >
                      <span>
                        {name} ({counts[name] || 0})
                      </span>
                    </CriticalRadio>
                  )
                })}
            </RadioGroup>
          </div>
        </div>

        {children?.map((child, index) => {
          return (
            <div
              key={`rich-text-${index}`}
              data-radio-selector-content
              style={index === defaultChecked ? { display: 'block' } : { display: 'none' }}
            >
              <RichText {...child} />
            </div>
          )
        })}
      </Card>
    </div>
  )
}

export default connect((state: FsCore.State, props: { children: any }) => {
  const selectedRadio = state.app?.location?.query?.selectedRadio
  const hasEligibleHits = state.productTables?.eligibilityResults?.length > 0

  return {
    ...props,
    selectedRadio,
    hasEligibleHits,
  }
})(RadioSelector)
